<template>
    <div>
        <b-table
            :items="limits"
            :fields="fields"
            bordered
            fixed
            table-class="table-va-middle"
            empty-text="Nenhum limite de sensor cadastrado."
            show-empty
            class="shadow-sm"
            :responsive="true"
            :per-page="perPage"
            :current-page="currentPage"
            primary-key="id"
        >
            <template v-slot:cell(sensor.name)="{ item }">
                <template v-if="isEditing(item)">
                    <b-form-select
                        ref="sensor"
                        :value="current.sensor.id"
                        :options="optionsSensors"
                        :disabled="saving"
                        @input="setCurrentSensor"
                    />
                </template>
                <template v-else>
                    {{ item.sensor.name }}
                </template>
            </template>
            <template v-slot:cell(condition)="{ item }">
                <template v-if="isEditing(item)">
                    <b-form-select
                        :value="current.condition"
                        :options="optionsConditions"
                        :disabled="saving"
                        @input="setCurrentProp('condition', $event)"
                    />
                </template>
                <template v-else>
                    {{ LimitConditions[item.condition] }}
                </template>
            </template>
            <template v-slot:cell(value)="{ item }">
                <template v-if="isEditing(item)">
                    <swx-number-input
                        name="current_value"
                        :value="current.value"
                        :disabled="saving"
                        @update="setCurrentProp('value', $event)"
                    />
                </template>
                <template v-else>
                    {{ format(item.value) }}
                </template>
            </template>
            <template v-slot:cell(kind)="{ item }">
                <template v-if="isEditing(item)">
                    <b-form-select
                        :value="current.kind"
                        :options="optionsKinds"
                        :disabled="saving"
                        @input="setCurrentProp('kind', $event)"
                    />
                </template>
                <template v-else>
                    {{ readingKinds[item.sensor.kind][item.kind] }}
                </template>
            </template>
            <template v-slot:cell(alert_level)="{ item }">
                <template v-if="isEditing(item)">
                    <b-form-select
                        :value="current.alert_level"
                        :options="optionsAlerts"
                        :disabled="saving"
                        @input="setCurrentProp('alert_level', $event)"
                    />
                </template>
                <template v-else>
                    <b-badge
                        :variant="getAlertVariant(item.alert_level)"
                        pill
                        class="px-3"
                    >
                        {{
                            AlertLevels[item.alert_level].toUpperCase()
                        }}</b-badge
                    >
                </template>
            </template>
            <template v-slot:cell(id)="{ item }">
                <template v-if="isEditing(item)">
                    <b-button
                        v-b-tooltip.hover.window.left="'Salvar limite'"
                        size="sm"
                        variant="secondary"
                        :disabled="saving"
                        class="mr-3"
                        @click="updateCurrent"
                    >
                        <b-spinner v-if="saving" small />
                        <i v-else class="fas fa-fw fa-save" />
                    </b-button>
                    <b-button
                        v-b-tooltip.hover.window.right="'Cancelar edição'"
                        size="sm"
                        variant="danger"
                        :disabled="saving"
                        @click="setCurrent(null)"
                        ><i class="fas fa-fw fa-times-circle"
                    /></b-button>
                </template>
                <template v-else>
                    <b-button
                        v-b-tooltip.hover.window.left="'Editar limite'"
                        size="sm"
                        class="mr-3"
                        variant="secondary"
                        :disabled="saving || !!current"
                        @click="setCurrent(item)"
                        ><i class="fas fa-fw fa-pencil-alt"
                    /></b-button>
                    <b-button
                        v-b-tooltip.hover.window.right="'Apagar limite'"
                        size="sm"
                        variant="danger"
                        :disabled="saving || !!current"
                        @click="showDeleteModal(item)"
                        ><i class="fas fa-fw fa-trash-alt"
                    /></b-button>
                </template>
            </template>
        </b-table>
        <b-pagination
            v-model="currentPage"
            :total-rows="limits.length"
            :per-page="perPage"
            first-text="Primeira página"
            last-text="Última página"
            align="center"
            :disabled="saving"
        />
        <sensor-limit-delete-modal
            ref="deleteModal"
            :source="source"
            :reading-kinds="readingKinds"
            @delete-limit="deleteLimit"
        />
    </div>
</template>

<script>
import hasProperty from "@/utils/hasProperty";
import { LimitConditions } from "@/modules/common/settings";
import settingsApi from "@/modules/settings/api";
import sleep from "@/utils/sleep";
import SwxNumberInput from "@/components/SwxNumberInput.vue";
import SensorLimitDeleteModal from "./SensorLimitDeleteModal";

export default {
    name: "SensorLimitTable",
    inject: [
        "getOptionsSensors",
        "optionsConditions",
        "LimitConditions",
        "format",
        "getOptionsKinds",
        "AlertLevels",
        "optionsAlerts",
        "getAlertVariant"
    ],
    components: {
        SwxNumberInput,
        SensorLimitDeleteModal
    },
    props: {
        source: {
            type: Number,
            required: true
        },
        sensors: {
            type: Array,
            required: true
        },
        limits: {
            type: Array,
            required: true
        },
        readingKinds: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            currentPage: 1,
            perPage: 5,
            current: null,
            saving: false
        };
    },
    computed: {
        fields() {
            const tdClass = (value, key, item) => {
                let baseClasses =
                    "p-0 align-middle text-center limits-row-height";
                if (this.isEditing(item)) {
                    baseClasses += " limits-editing bg-light";
                }
                return baseClasses;
            };

            return [
                {
                    label: "Sensor",
                    key: "sensor.name",
                    tdClass,
                    sortable: true
                },
                {
                    label: "Condição",
                    key: "condition",
                    tdClass
                },
                {
                    label: "Valor",
                    key: "value",
                    tdClass,
                    sortable: true
                },
                {
                    label: "Tipo (unidade)",
                    key: "kind",
                    tdClass,
                    sortable: true
                },
                {
                    label: "Nível de alerta",
                    key: "alert_level",
                    tdClass,
                    sortable: true
                },
                {
                    label: "Gerenciar",
                    key: "id",
                    tdClass
                }
            ];
        },
        optionsSensors() {
            return this.getOptionsSensors();
        },
        optionsKinds() {
            return this.getOptionsKinds(this.current.sensor.kind);
        }
    },
    methods: {
        isEditing(item) {
            if (!this.current) {
                return false;
            }
            return this.current.id === item.id;
        },
        setCurrentSensor(sensorId) {
            const sensor = this.sensors.find(sensor => sensor.id === sensorId);
            const kinds = this.readingKinds[sensor.kind];

            this.setCurrentProp("sensor", {
                ...sensor
            });

            if (!hasProperty(kinds, this.current.kind)) {
                this.setCurrentProp("kind", Object.keys(kinds)[0]);
            }
        },
        setCurrentProp(prop, value) {
            if (
                (prop === "kind" ||
                    prop === "condition" ||
                    prop === "alert_level") &&
                typeof value === "string"
            ) {
                value = parseInt(value, 10);
            }
            this.current[prop] = value;
        },
        setCurrent(item) {
            if (item) {
                this.current = {
                    ...item,
                    sensor: {
                        ...item.sensor
                    }
                };
                this.$nextTick(() => {
                    this.$refs.sensor.focus();
                });
            } else {
                this.current = null;
            }
        },
        async updateCurrent() {
            this.$root.$emit("bv::hide::tooltip");
            this.saving = true;
            await this.$nextTick();
            await sleep(1000);

            const { sensor, ...data } = this.current;

            const response = await settingsApi.sensors.updateLimit(
                this.source,
                {
                    sensor_id: sensor.id,
                    ...data
                }
            );

            this.$bvToast.toast(`Limite atualizado com sucesso.`, {
                title: "Aviso",
                variant: "info",
                noCloseButton: true
            });
            const index = this.limits.findIndex(
                limit => limit.id === this.current.id
            );
            this.$emit("update-limit", { index, limit: response.data });
            this.setCurrent(null);
            this.saving = false;
        },
        showDeleteModal(limit) {
            this.$refs.deleteModal.show(limit);
        },
        deleteLimit(limit) {
            const index = this.limits.findIndex(item => item.id === limit.id);
            this.$emit("delete-limit", { index, limit });
        }
    }
};
</script>

<style lang="scss" scoped>
$limits-row-height: 55px;

/deep/ {
    .limits-row-height {
        height: $limits-row-height;
        max-height: $limits-row-height;
    }
    .limits-editing {
        input.form-control,
        .custom-select {
            height: 100%;
            border-radius: 0;
            border: none;
            background-color: transparent;
            &:focus {
                box-shadow: inset 0 0 4px 2px rgba(0, 123, 255, 0.25);
            }
        }
    }
}
</style>
